<template>
  <CSidebarNav class="sidebar-slider">
    <!-- Space -->
    <CNavItem>
      <router-link to="/dashboard" class="nav-link">
        <span class="material-symbols-outlined nav-icon">rocket_launch</span>
        <span class="link-text">{{ $t('sidebar.Space') }}</span>
      </router-link>
    </CNavItem>

    <!-- CRM -->
    <CNavGroup v-if="!isRecruiter" :visible="openGroups.crm" @click="toggleGroup('crm')">
      <template #togglerContent>
        <span class="material-symbols-outlined nav-icon">lightbulb</span>
        <span class="link-text">{{ $t('sidebar.crm') }}</span>
      </template>
      <CNavItem>
        <router-link to="/leads" class="nav-link">
          <span class="material-symbols-outlined nav-icon">person_add</span>
          <span class="link-text">{{ $t('sidebar.leadsManagement') }}</span>
        </router-link>
      </CNavItem>
      <CNavItem>
        <router-link to="/clients" class="nav-link">
          <span class="material-symbols-outlined nav-icon">group</span>
          <span class="link-text">{{ $t('sidebar.clients') }}</span>
        </router-link>
      </CNavItem>
      <CNavItem>
        <router-link to="/projects" class="nav-link">
          <span class="material-symbols-outlined nav-icon">folder</span>
          <span class="link-text">{{ $t('sidebar.projects') }}</span>
        </router-link>
      </CNavItem>
      <CNavItem>
        <router-link to="/invoicing" class="nav-link">
          <span class="material-symbols-outlined nav-icon">receipt</span>
          <span class="link-text">{{ $t('sidebar.invoicing') }}</span>
        </router-link>
      </CNavItem>
    </CNavGroup>

    <!-- HR Group (for recruiters only) -->
    <CNavGroup v-if="isRecruiter" :visible="openGroups.hr" @click="toggleGroup('hr')">
      <template #togglerContent>
        <span class="material-symbols-outlined nav-icon">supervisor_account</span>
        <span class="link-text">{{ $t('sidebar.hr') }}</span>
      </template>
    </CNavGroup>

    <!-- Reports -->
    <CNavItem>
      <router-link to="/worktime-report" class="nav-link">
        <span class="material-symbols-outlined nav-icon">description</span>
        <span class="link-text">{{ $t('sidebar.reports') }}</span>
      </router-link>
    </CNavItem>

    <!-- Attendance -->
    <CNavItem>
      <router-link to="/attendance" class="nav-link">
        <span class="material-symbols-outlined nav-icon">schedule</span>
        <span class="link-text">{{ $t('sidebar.attendance') }}</span>
      </router-link>
    </CNavItem>

    <!-- Calendar -->
    <CNavItem>
      <router-link to="/calendar" class="nav-link">
        <span class="material-symbols-outlined nav-icon">event</span>
        <span class="link-text">{{ $t('sidebar.calendar') }}</span>
      </router-link>
    </CNavItem>

    <!-- Team -->
    <CNavItem>
      <router-link to="/team" class="nav-link">
        <span class="material-symbols-outlined nav-icon">groups</span>
        <span class="link-text">{{ $t('sidebar.team') }}</span>
      </router-link>
    </CNavItem>

    <!-- Wiki -->
    <CNavItem>
      <router-link to="/wiki" class="nav-link">
        <span class="material-symbols-outlined nav-icon">menu_book</span>
        <span class="link-text">{{ $t('sidebar.wiki') }}</span>
      </router-link>
    </CNavItem>

    <!-- Tasks -->
    <CNavItem>
      <router-link to="/tasks" class="nav-link">
        <span class="material-symbols-outlined nav-icon">view_kanban</span>
        <span class="link-text">{{ $t('sidebar.tasks') }}</span>
      </router-link>
    </CNavItem>

    <!-- Inventory -->
    <CNavItem>
      <router-link to="/inventory" class="nav-link">
        <span class="material-symbols-outlined nav-icon">warehouse</span>
        <span class="link-text">{{ $t('sidebar.inventory') }}</span>
      </router-link>
    </CNavItem>

    <!-- Review360 -->
    <CNavGroup v-if="isReview" :visible="openGroups.review360" @click="toggleGroup('review360')">
      <template #togglerContent>
        <span class="material-symbols-outlined nav-icon">content_paste</span>
        <span class="link-text">{{ $t('projectTeams.review360') }}</span>
      </template>
      <CNavItem>
        <router-link to="/selfassessment" class="nav-link">
          <span class="material-symbols-outlined nav-icon">person</span>
          <span class="link-text">{{ $t('review360.self') }}</span>
        </router-link>
      </CNavItem>
      <CNavItem>
        <router-link to="/teamassessment" class="nav-link">
          <span class="material-symbols-outlined nav-icon">groups</span>
          <span class="link-text">{{ $t('review360.all') }}</span>
        </router-link>
      </CNavItem>
    </CNavGroup>

    <!-- Results -->
    <CNavGroup v-if="!isReview" :visible="openGroups.results" @click="toggleGroup('results')">
      <template #togglerContent>
        <span class="material-symbols-outlined nav-icon">inventory</span>
        <span class="link-text">{{ $t('sidebar.results') }}</span>
      </template>
      <CNavItem>
        <router-link to="/userresults" class="nav-link">
          <span class="material-symbols-outlined nav-icon">assignment_ind</span>
          <span class="link-text">{{ $t('sidebar.self_results') }}</span>
        </router-link>
      </CNavItem>
      <CNavItem>
        <router-link to="/teamresults" class="nav-link">
          <span class="material-symbols-outlined nav-icon">groups</span>
          <span class="link-text">{{ $t('results.teams') }}</span>
        </router-link>
      </CNavItem>
    </CNavGroup>

    <!-- Review360 Settings -->
    <CNavItem v-if="currentRoleAccesses.includes('Assessment_questions')">
      <router-link to="/review360settings" class="nav-link">
        <span class="material-symbols-outlined nav-icon">settings</span>
        <span class="link-text">{{ $t('sidebar.review360settings') }}</span>
      </router-link>
    </CNavItem>
  </CSidebarNav>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

const isReview = computed(() => store.getters['teams/isReviewRunning'])
const isRecruiter = computed(() =>
  store.getters['authorization/getCurrentUser'].roles?.some(r => r.id === 14)
)
const currentRoleAccesses = computed(() =>
  store.getters['authorization/getCurrentUser'].role_accesses?.map(r => r.entity) || []
)

const openGroups = ref({
  crm: false,
  hr: false,
  review360: false,
  results: false
})

function toggleGroup(group) {
  openGroups.value[group] = !openGroups.value[group]
}
</script>
<style lang="scss">
@use '@/styles/sidebar.scss' as *;
</style>